/* eslint-disable camelcase */
import React from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import useStyles from 'isomorphic-style-loader/useStyles';
import { useSelector, useDispatch } from 'react-redux';

// app: actions
import saveUserSettings from 'actions/user/saveUserSettings';

// app: constants
import NUTRITION_DIET_OPTIONS, {
  NUTRITION_DIET_EXCLUSIVE_GROUP,
  NUTRITION_DIET_NO_PREFERENCE_ID,
  NUTRITION_DIET_NO_PREFERENCE_LABEL,
  normalizeDietSelection,
} from 'constants/nutrition-diet-options';

// assets
import CssUserSettings from '../UserSettings.scss';
import CssNutritionSettings from './NutritionSettings.scss';

const EXCLUSIVE_ID_SET = new Set(NUTRITION_DIET_EXCLUSIVE_GROUP);

const DIET_OPTION_ROWS = [
  NUTRITION_DIET_OPTIONS.filter(option => EXCLUSIVE_ID_SET.has(option.id)),
  NUTRITION_DIET_OPTIONS.filter(option => !EXCLUSIVE_ID_SET.has(option.id)),
].filter(row => row.length > 0);

const NO_PREFERENCE_ROW = [
  { id: NUTRITION_DIET_NO_PREFERENCE_ID, label: NUTRITION_DIET_NO_PREFERENCE_LABEL },
];

export function DietButtonMultiSelect({
  name,
  selectedIds,
  onChange,
  disabled,
  allowNoPreference,
}) {
  useStyles(CssNutritionSettings);

  const effectiveIds = normalizeDietSelection(selectedIds);
  const dietIds = effectiveIds.filter(id => id !== NUTRITION_DIET_NO_PREFERENCE_ID);

  const rows = allowNoPreference ? [...DIET_OPTION_ROWS, NO_PREFERENCE_ROW] : DIET_OPTION_ROWS;

  const toggle = id => {
    if (id === NUTRITION_DIET_NO_PREFERENCE_ID) {
      // Choosing "no preference" clears every diet; choosing a diet (below) clears it again.
      onChange(effectiveIds.includes(id) ? [] : [id]);
    } else if (dietIds.includes(id)) {
      onChange(dietIds.filter(selectedId => selectedId !== id));
    } else if (NUTRITION_DIET_EXCLUSIVE_GROUP.includes(id)) {
      onChange([
        ...dietIds.filter(selectedId => !NUTRITION_DIET_EXCLUSIVE_GROUP.includes(selectedId)),
        id,
      ]);
    } else {
      onChange([...dietIds, id]);
    }
  };

  return (
    <div className="nutrition-settings__options" role="group" aria-label={name}>
      {rows.map(row => (
        <div className="nutrition-settings__row" key={row[0].id}>
          {row.map(option => {
            const isSelected = effectiveIds.includes(option.id);
            return (
              <button
                type="button"
                key={option.id}
                className={classNames('nutrition-settings__button', {
                  active: isSelected,
                })}
                onClick={() => toggle(option.id)}
                aria-pressed={isSelected}
                disabled={disabled}
              >
                {option.label}
              </button>
            );
          })}
        </div>
      ))}
    </div>
  );
}

DietButtonMultiSelect.propTypes = {
  name: PropTypes.string.isRequired,
  selectedIds: PropTypes.arrayOf(PropTypes.string).isRequired,
  onChange: PropTypes.func.isRequired,
  disabled: PropTypes.bool,
  allowNoPreference: PropTypes.bool,
};

DietButtonMultiSelect.defaultProps = {
  disabled: false,
  allowNoPreference: false,
};

function NutritionSettings() {
  useStyles(CssUserSettings, CssNutritionSettings);
  const dispatch = useDispatch();
  const settings = useSelector(state => state.user.settings);
  const settingsResolved = useSelector(state => state.user.settingsResolved);

  const limitIds = Array.isArray(settings?.nutrition_diet_limit)
    ? settings.nutrition_diet_limit
    : [];
  const preferIds = Array.isArray(settings?.nutrition_diet_prefer)
    ? settings.nutrition_diet_prefer
    : [];

  const handleLimitChange = newIds => {
    dispatch(saveUserSettings({ nutrition_diet_limit: newIds }));
  };

  const handlePreferChange = newIds => {
    dispatch(saveUserSettings({ nutrition_diet_prefer: newIds }));
  };

  return (
    <div>
      <div className="nutrition-settings__group">
        <div className="nutrition-settings__group-label">Limit search to</div>
        <DietButtonMultiSelect
          name="Limit search to"
          selectedIds={limitIds}
          onChange={handleLimitChange}
          disabled={!settingsResolved}
        />
      </div>
      <div className="nutrition-settings__group">
        <div className="nutrition-settings__group-label">Prefer</div>
        <DietButtonMultiSelect
          name="Prefer"
          allowNoPreference
          selectedIds={preferIds}
          onChange={handlePreferChange}
          disabled={!settingsResolved}
        />
      </div>
    </div>
  );
}

export default NutritionSettings;
